import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Shield, Star, Award } from 'lucide-react';
import { companyInfo } from '../data';
import { GeneralContactForm } from '../components/ContactForm';

export default function ContactUsPage() {
  return (
    <div className="pt-24 lg:pt-32">
      {/* Dark Header Section */}
      <div className="bg-slate-900 py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4">
            Kontakt <span className="text-sky-500">KC Detailer</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Har du spørgsmål eller brug for et uforpligtende tilbud? Vi sidder klar til at hjælpe dig med professionel rådgivning om bilpleje.
          </p>
        </div>
      </div>

      {/* Main Content Section */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <div>
                <h2 className="text-3xl font-black text-slate-900 mb-8 uppercase tracking-tight">Vores Information</h2>
                <div className="grid gap-8">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-sky-500 shrink-0 border border-slate-100">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">Ring til os</p>
                      <a href={`tel:${companyInfo.phone}`} className="text-xl font-bold text-slate-900 hover:text-sky-500 transition-colors">
                        {companyInfo.phone}
                      </a>
                      <p className="text-slate-500 mt-1">Vi svarer hurtigst muligt inden for åbningstiden.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-sky-500 shrink-0 border border-slate-100">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">Send en mail</p>
                      <a href={`mailto:${companyInfo.email}`} className="text-xl font-bold text-slate-900 hover:text-sky-500 transition-colors">
                        {companyInfo.email}
                      </a>
                      <p className="text-slate-500 mt-1">Vi bestræber os på at besvare alle mails inden for 24 timer.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-sky-500 shrink-0 border border-slate-100">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">Besøg os</p>
                      <p className="text-xl font-bold text-slate-900">{companyInfo.address}</p>
                      <p className="text-slate-500 mt-1">Professionelt værksted med fokus på detaljen.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center text-sky-500 shrink-0 border border-slate-100">
                      <Clock size={24} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">Åbningstider</p>
                      <div className="text-slate-900 font-bold">
                        <p>Mandag - Fredag: 08:00 - 17:00</p>
                        <p>Lørdag: Efter aftale</p>
                        <p>Søndag: Lukket</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Choose Us Content */}
              <div className="bg-slate-50 rounded-[2.5rem] p-10 border border-slate-100">
                <h3 className="text-2xl font-black text-slate-900 mb-6 uppercase">Hvorfor vælge KC Detailer?</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="mt-1 text-sky-500"><Shield size={20} /></div>
                    <p className="text-slate-600"><span className="font-bold text-slate-900">Kvalitetsgaranti:</span> Vi bruger kun de bedste produkter på markedet for at sikre et holdbart og fejlfrit resultat.</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="mt-1 text-sky-500"><Star size={20} /></div>
                    <p className="text-slate-600"><span className="font-bold text-slate-900">Ekspertise:</span> Med mange års erfaring inden for bilpleje ved vi præcis, hvordan vi skal håndtere din bil.</p>
                  </div>
                  <div className="flex gap-4">
                    <div className="mt-1 text-sky-500"><Award size={20} /></div>
                    <p className="text-slate-600"><span className="font-bold text-slate-900">Detaljeorienteret:</span> Vi går ikke på kompromis. Hver eneste krog og flade får vores fulde opmærksomhed.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-900 mb-4 uppercase tracking-tight">Send os en besked</h2>
                <p className="text-slate-600">Udfyld formularen herunder, så kontakter vi dig hurtigst muligt med svar på din henvendelse.</p>
              </div>
              <div className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100">
                <GeneralContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
